import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

function Login() {
  const [loginType, setLoginType] = useState('salon'); // 'admin' or 'salon'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [salonNumber, setSalonNumber] = useState('');
  const [loginCode, setLoginCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [showWebLoginGuide, setShowWebLoginGuide] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, salonLoginWithCode, currentUser, userRole } = useAuth();

  useEffect(() => {
    if (!showWebLoginGuide) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') setShowWebLoginGuide(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showWebLoginGuide]);

  // Redirect if already logged in
  if (currentUser) {
    if (userRole === 'salon') {
      return <Navigate to="/salon/dashboard" />;
    }
    return <Navigate to="/bookings" />;
  }

  async function handleAdminLogin(e) {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await login(email, password);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSalonLogin(e) {
    e.preventDefault();

    if (!salonNumber) {
      setError('Please enter your salon number');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Call validateSalonPhone Cloud Function (now local, not external)
      // Import dynamically to avoid circular deps
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      
      const validateSalonPhone = httpsCallable(functions, 'validateSalonPhone');
      const result = await validateSalonPhone({ salonNumber: salonNumber });
      
      if (result.data && result.data.success) {
        // Salon validated successfully, show code input
        setShowCodeInput(true);
        setError('');
      } else {
        setError(result.data?.message || 'Invalid salon number or salon not approved');
      }
      
    } catch (error) {
      setError('Unable to validate salon. Please check your salon number and try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e) {
    e.preventDefault();

    if (!loginCode) {
      setError('Please enter the login code');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Call the salonLoginWithCode from AuthContext
      await salonLoginWithCode(salonNumber, loginCode);
      
      // Redirect will happen automatically via Navigate component
    } catch (error) {
      setError(error.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleLoginTypeChange(type) {
    setLoginType(type);
    setError('');
    setSalonNumber('');
    setLoginCode('');
    setShowCodeInput(false);
    setShowWebLoginGuide(false);
    setEmail('');
    setPassword('');
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Ulloor Styles</h1>
          <p>Welcome Back</p>
        </div>

        {/* Login Type Tabs */}
        <div className="login-tabs">
          <button
            className={`tab-btn ${loginType === 'salon' ? 'active' : ''}`}
            onClick={() => handleLoginTypeChange('salon')}
            type="button"
          >
            Salon
          </button>
          <button
            className={`tab-btn ${loginType === 'admin' ? 'active' : ''}`}
            onClick={() => handleLoginTypeChange('admin')}
            type="button"
          >
            Admin
          </button>
        </div>
        
        {/* Salon Login Form */}
        {loginType === 'salon' && (
          <form onSubmit={showCodeInput ? handleVerifyCode : handleSalonLogin} className="login-form">
            {!showCodeInput ? (
              <>
                <div className="form-group">
                  <label htmlFor="salonNumber">Salon Number</label>
                  <input
                    type="text"
                    id="salonNumber"
                    value={salonNumber}
                    onChange={(e) => setSalonNumber(e.target.value)}
                    placeholder="Enter your salon number"
                    required
                  />
                  <small className="input-hint">Enter your registered salon number</small>
                </div>
                
                {error && <div className="error-message">{error}</div>}
                
                <button 
                  type="submit" 
                  className="login-btn"
                  disabled={loading}
                >
                  {loading ? 'Validating...' : 'Continue'}
                </button>
              </>
            ) : (
              <>
                <div className="form-group">
                  <div className="form-label-row">
                    <label htmlFor="loginCode">Login Code</label>
                    <button
                      type="button"
                      className="web-login-info-btn"
                      onClick={() => setShowWebLoginGuide(true)}
                      aria-label="How to get your web login code"
                      aria-haspopup="dialog"
                    >
                      <span className="web-login-info-btn__icon" aria-hidden>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                          <path
                            d="M12 16v-4M12 8h.01"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                  <input
                    type="text"
                    id="loginCode"
                    value={loginCode}
                    onChange={(e) => setLoginCode(e.target.value)}
                    placeholder="Enter code from your app"
                    required
                  />
                  <small className="input-hint">
                    Enter the login code from your Ulloor Styles mobile app
                  </small>
                </div>
                
                {error && <div className="error-message">{error}</div>}
                
                <button 
                  type="submit" 
                  className="login-btn"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </button>

                <button
                  type="button"
                  className="back-btn"
                  onClick={() => {
                    setShowCodeInput(false);
                    setLoginCode('');
                    setError('');
                    setShowWebLoginGuide(false);
                  }}
                >
                  Change Salon Number
                </button>

                {showWebLoginGuide && (
                  <div
                    className="web-login-guide-overlay"
                    onClick={() => setShowWebLoginGuide(false)}
                    role="presentation"
                  >
                    <div
                      className="web-login-guide-modal"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="web-login-guide-title"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h2 id="web-login-guide-title" className="web-login-guide-title">
                        How to get your web login code
                      </h2>
                      <ol className="web-login-guide-steps">
                        <li>Open the Ulloor Styles app on your mobile device.</li>
                        <li>Go to <strong>Profile</strong>.</li>
                        <li>Find <strong>Generate web login code</strong> and tap it.</li>
                        <li>You will receive a 6-digit code. Enter it here and continue.</li>
                      </ol>
                      <p className="web-login-guide-note">
                        The code expires in 2 minutes. If it expires, generate a new code from the app.
                      </p>
                      <button
                        type="button"
                        className="web-login-guide-dismiss"
                        onClick={() => setShowWebLoginGuide(false)}
                      >
                        Got it
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="login-footer">
              <p>Only approved salons can access</p>
            </div>
          </form>
        )}

        {/* Admin Login Form */}
        {loginType === 'admin' && (
          <form onSubmit={handleAdminLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button 
              type="submit" 
              className="login-btn"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>

            <div className="login-footer">
              <p>Access restricted to admin users only</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;















